import { useEffect, useState, useCallback, useRef, lazy, Suspense } from "react";
import api from "@/lib/api";
import { timeAgo, formatApiError, limitWords } from "@/lib/format";
import { useProfileViewer } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { RespondModal } from "@/components/RespondModal";
import { MyAnswersButton } from "@/components/MyAnswers";
import { Globe as GlobeIcon, RefreshCw, Trash2, Flag } from "lucide-react";
import { toast } from "sonner";

// Code-split the heavy three.js globe so it loads only when opened (keeps it out of the main bundle)
const GlobeModal = lazy(() => import("@/components/GlobeModal").then((m) => ({ default: m.GlobeModal })));

export default function AnswerPage() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [subjectQuery, setSubjectQuery] = useState("");
  const [sort, setSort] = useState("recent");
  const [sortOpen, setSortOpen] = useState(false);
  const [randomMap, setRandomMap] = useState({});
  const [country, setCountry] = useState("");
  const [globeOpen, setGlobeOpen] = useState(false);
  const { viewProfile } = useProfileViewer();
  const { user } = useAuth();
  const isGod = user?.is_god;
  const [flagInq, setFlagInq] = useState(null);
  const [flagText, setFlagText] = useState("");
  const [flagAmount, setFlagAmount] = useState("");
  const [flagBusy, setFlagBusy] = useState(false);
  const [deleteInq, setDeleteInq] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const sortRef = useRef(sort);
  useEffect(() => { sortRef.current = sort; }, [sort]);

  const sortOptions = [
    { k: "recent", label: "recent" },
    { k: "random", label: "random" },
    { k: "size", label: "size" },
  ];
  const currentSortLabel = sortOptions.find((o) => o.k === sort)?.label;

  const shuffle = useCallback((items) => {
    const m = {};
    items.forEach((i) => { m[i.id] = Math.random(); });
    setRandomMap(m);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/inquiries");
    setInquiries(data);
    if (sortRef.current === "random") {
      shuffle(data);
    } else {
      setRandomMap((prev) => {
        const m = { ...prev };
        data.forEach((i) => { if (m[i.id] === undefined) m[i.id] = Math.random(); });
        return m;
      });
    }
    setLoading(false);
  }, [shuffle]);

  useEffect(() => { load(); }, [load]);

  const claimAndOpen = async (inq) => {
    try {
      const { data } = await api.post(`/inquiries/${inq.id}/claim`);
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load();
    }
  };

  const openFlag = (inq) => {
    setFlagInq(inq);
    setFlagText("");
    setFlagAmount("");
  };

  const submitFlag = async () => {
    const amount = Number(flagAmount);
    if (flagBusy || !flagInq || !Number.isInteger(amount) || amount < 1) return;
    setFlagBusy(true);
    try {
      const { data } = await api.post(`/inquiries/${flagInq.id}/flag`, { category: "genius", appreciation: flagText, provisions: amount });
      toast.success(data.rewarded ? `sent to remarkable minds · +${data.rewarded} provisions` : "sent to remarkable minds");
      setFlagInq(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setFlagBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (deleteBusy || !deleteInq) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/inquiries/${deleteInq.id}`);
      toast.success("ask deleted");
      setDeleteInq(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleteBusy(false);
    }
  };

  const sq = subjectQuery.trim().toLowerCase();
  const cq = country.trim().toLowerCase();

  const filtered = inquiries
    .filter((inq) => {
      const matchesSubject = !sq || (inq.subject || "").toLowerCase().includes(sq);
      const loc = (inq.author?.location || "").toLowerCase();
      const matchesCountry = !cq || loc === cq || loc.includes(cq) || (loc.length >= 4 && cq.includes(loc));
      return matchesSubject && matchesCountry;
    })
    .sort((a, b) => {
      if (sort === "size") return (b.word_count || 0) - (a.word_count || 0);
      if (sort === "recent") return new Date(b.created_at) - new Date(a.created_at);
      return (randomMap[a.id] ?? 0) - (randomMap[b.id] ?? 0); // random
    });

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-8">
        <h1 className="font-head font-light text-4xl tracking-tighter" style={{ color: "var(--text-primary)" }}>
          Answer
        </h1>
        <div className="flex items-center gap-3">
        <MyAnswersButton />
        <button
          type="button"
          data-testid="answer-refresh-btn"
          onClick={() => load()}
          title="Refresh"
          className="btn shrink-0 flex items-center justify-center w-[46px] h-[46px]"
        >
          <RefreshCw size={18} strokeWidth={1.5} className={loading ? "animate-spin" : undefined} />
        </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-10">
        <input
          data-testid="answer-subject-input"
          value={subjectQuery}
          onChange={(e) => setSubjectQuery(limitWords(e.target.value, subjectQuery, 5))}
          placeholder="subject"
          className="flex-1 min-w-0 px-4 py-3 text-base"
          style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-primary)" }}
        />
        <button
          type="button"
          data-testid="answer-globe-btn"
          onClick={() => setGlobeOpen(true)}
          title="Filter by country"
          className="btn shrink-0 flex items-center justify-center w-full sm:w-[3.25rem] h-[50px]"
          style={{ color: country ? "var(--accent)" : undefined }}
        >
          <GlobeIcon size={20} strokeWidth={1.5} />
        </button>
        <div className="relative shrink-0">
          <button
            type="button"
            data-testid="answer-sort-select"
            onClick={() => setSortOpen((v) => !v)}
            className="btn w-full sm:w-52 px-4 py-3 text-base flex items-center justify-between gap-3"
          >
            <span className="whitespace-nowrap">{currentSortLabel}</span>
            <span style={{ transform: sortOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}>▾</span>
          </button>
          {sortOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setSortOpen(false)} />
              <div
                className="absolute z-40 left-0 right-0 mt-1 flex flex-col gap-1 p-1"
                style={{ background: "var(--bg-primary)" }}
                data-testid="answer-sort-options"
              >
                {sortOptions.map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    data-testid={`answer-sort-${o.k}`}
                    onClick={() => { if (o.k === "random") shuffle(inquiries); setSort(o.k); setSortOpen(false); }}
                    className="btn w-full px-4 py-2 text-sm whitespace-nowrap"
                    style={sort === o.k ? { background: "var(--accent)", color: "#121212" } : undefined}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {country && (
        <div className="flex items-center gap-3 mb-8 -mt-4" data-testid="answer-country-chip">
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>country:</span>
          <span className="text-sm px-3 py-1 flex items-center gap-2" style={{ border: "1px solid var(--accent)", color: "var(--accent)" }}>
            {country}
            <button onClick={() => setCountry("")} data-testid="answer-country-clear" className="hover:opacity-100 opacity-70">×</button>
          </span>
        </div>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
      ) : inquiries.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="answer-empty">
          No asks to answer yet.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="answer-no-match">
          No asks match your search.
        </p>
      ) : (
        <ul>
          {filtered.map((inq, i) => {
            return (
            <li
              key={inq.id}
              data-testid={`answer-item-${inq.id}`}
              className="fade-in-up transition-colors duration-200 cursor-pointer group"
              style={{ borderBottom: "1px solid var(--border)", background: "transparent", animationDelay: `${i * 40}ms` }}
              onClick={() => claimAndOpen(inq)}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-secondary)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div className="py-6 px-4">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {inq.subject && (
                    <span
                      data-testid={`answer-subject-tag-${inq.id}`}
                      className="inline-block text-[10px] uppercase tracking-widest px-2 py-1"
                      style={{ border: "1px solid var(--border)", color: "var(--accent)" }}
                    >
                      {inq.subject}
                    </span>
                  )}
                </div>
                <p className="long-text font-head font-light text-xl leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  {inq.text}
                </p>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); viewProfile(inq.author?.id); }}
                    data-testid={`answer-author-${inq.id}`}
                    className="flex items-center gap-2 hover:opacity-100 opacity-70 transition-opacity"
                  >
                    <span className="text-xs tracking-wide" style={{ color: "var(--text-secondary)" }}>
                      {inq.author?.name || "unknown"}
                    </span>
                  </button>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>· {timeAgo(inq.created_at)}</span>
                  {isGod && (
                    <span className="flex items-center gap-2 ml-auto">
                      <button
                        onClick={(e) => { e.stopPropagation(); if (!inq.flagged) openFlag(inq); }}
                        disabled={inq.flagged}
                        data-testid={`answer-god-flag-${inq.id}`}
                        title={inq.flagged ? "Already flagged" : "Flag this ask"}
                        className="btn flex items-center justify-center w-[38px] h-[38px] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Flag size={15} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteInq(inq); }}
                        data-testid={`answer-god-delete-${inq.id}`}
                        title="Delete this ask"
                        className="btn flex items-center justify-center w-[38px] h-[38px]"
                      >
                        <Trash2 size={15} strokeWidth={1.5} />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </li>
            );
          })}
        </ul>
      )}

      {selected && (
        <RespondModal
          inquiry={selected}
          onClose={() => setSelected(null)}
          onSubmitted={() => load()}
          onReleased={() => {}}
        />
      )}

      {globeOpen && (
        <Suspense fallback={null}>
          <GlobeModal
            onClose={() => setGlobeOpen(false)}
            onApply={(c) => { setCountry(c); setGlobeOpen(false); }}
          />
        </Suspense>
      )}

      {flagInq && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setFlagInq(null)}
          data-testid="flag-overlay"
        >
          <div
            className="w-full max-w-md p-8 max-h-[85vh] flex flex-col"
            style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="flag-modal"
          >
            <div className="flex justify-between items-start mb-6 shrink-0">
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>send to remarkable minds</span>
              <button onClick={() => setFlagInq(null)} data-testid="flag-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>[ close ]</button>
            </div>
            <p className="long-text-sm text-sm leading-relaxed mb-6" style={{ color: "var(--text-secondary)" }} data-testid="flag-ask-preview">
              {flagInq.text}
            </p>
            <label className="text-xs uppercase tracking-widest shrink-0" style={{ color: "var(--text-muted)" }}>appreciation</label>
            <textarea
              data-testid="flag-appreciation-input"
              value={flagText}
              onChange={(e) => setFlagText(limitWords(e.target.value, flagText, 1000))}
              rows={4}
              placeholder="why is this remarkable…"
              className="w-full mt-2 p-3 text-sm resize-none shrink-0"
              style={{ background: "var(--bg-primary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
            />
            <label className="text-xs uppercase tracking-widest shrink-0 mt-6" style={{ color: "var(--text-muted)" }}>provisions to award</label>
            <input
              data-testid="flag-provisions-input"
              type="number"
              min={1}
              value={flagAmount}
              onChange={(e) => setFlagAmount(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="1"
              className="w-full mt-2 px-3 py-3 text-sm shrink-0"
              style={{ background: "var(--bg-primary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
            />
            <button onClick={submitFlag} disabled={flagBusy || !(Number(flagAmount) >= 1)} data-testid="flag-submit" className="btn w-full py-3 mt-6 text-sm tracking-widest shrink-0">
              {flagBusy ? "…" : "FLAG"}
            </button>
          </div>
        </div>
      )}

      {deleteInq && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setDeleteInq(null)}
          data-testid="delete-ask-overlay"
        >
          <div
            className="w-full max-w-md p-8"
            style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-ask-modal"
          >
            <p className="text-base mb-3" style={{ color: "var(--text-secondary)" }} data-testid="delete-ask-text">
              delete this ask from the pile entirely?
            </p>
            <p className="long-text text-xs leading-relaxed mb-8" style={{ color: "var(--text-muted)" }}>
              {deleteInq.text}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteInq(null)} data-testid="delete-ask-cancel" className="btn flex-1 py-3 text-sm tracking-widest">CANCEL</button>
              <button onClick={confirmDelete} disabled={deleteBusy} data-testid="delete-ask-confirm" className="btn flex-1 py-3 text-sm tracking-widest">{deleteBusy ? "…" : "DELETE"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
